import React, { memo, useCallback, useMemo } from 'react';

import { Button } from '@Sage/hoi-poi-ui';
import { isEmptyObject } from '@Sage/ssm-utils';
import { PdfViewer as Web5PdfViewer } from '@Sage/ssm-web5';

import { getLiteral } from 'utils/getLiteral';
import { logEvent } from 'utils/tracking';

import SignatureLocation from './SignatureLocation';
import Signatures from './Signatures';
import './styles.scss';

const PdfViewer = memo(
    ({
        currentPage,
        fileUrl,
        getError,
        initialScale = 1,
        signers,
        signatureState,
        signatureDispatch,
        addSignature,
        externalRef,
        fullScreenEventPayload,
    }) => {
        const resolvedFileUrl = fileUrl || signatureState?.fileUrl;

        const handleError = useCallback(() => {
            getError?.();
        }, [getError]);

        const handleViewerStateChange = useCallback(
            (payload) => {
                signatureDispatch?.({
                    type: 'updateViewerState',
                    payload,
                });
            },
            [signatureDispatch],
        );

        const handleFullscreenEnter = useCallback(() => {
            fullScreenEventPayload && logEvent(fullScreenEventPayload);
        }, [fullScreenEventPayload]);

        const renderOverlay = useCallback(
            ({ page }) => {
                if (!signatureState) {
                    return null;
                }

                return signatureState.isEditing !== false ? (
                    <SignatureLocation
                        signatureDispatch={signatureDispatch}
                        signatureState={signatureState}
                        setPage={() => {}}
                    />
                ) : (
                    <Signatures pageNumber={page} signers={signers} />
                );
            },
            [signatureDispatch, signatureState, signers],
        );

        const toolbarExtra = useMemo(() => {
            const showAddSignature =
                signatureState && signatureState.isEditing !== false ? true : false;

            if (!showAddSignature) {
                return null;
            }

            const page = signatureState?.pdfViewerState?.page;
            const disableAddSignature =
                signatureState.currentSignatures &&
                !isEmptyObject(signatureState.currentSignatures[page - 1]);

            return (
                <div className="fm-pdf-viewer-toolbar__items">
                    <Button
                        icon="editArea"
                        size="small"
                        type="secondary"
                        className="fm-pdf-viewer-toolbar__item--button"
                        onClick={addSignature}
                        isDisabled={disableAddSignature}
                    >
                        {getLiteral('action_extra_signature')}
                    </Button>
                </div>
            );
        }, [addSignature, signatureState]);

        return (
            <Web5PdfViewer
                currentPage={currentPage}
                externalRef={externalRef}
                fileUrl={resolvedFileUrl}
                initialScale={initialScale}
                onError={handleError}
                onFullscreenEnter={handleFullscreenEnter}
                onViewerStateChange={handleViewerStateChange}
                renderOverlay={renderOverlay}
                toolbarExtra={toolbarExtra}
            />
        );
    },
);

export default PdfViewer;
